// @ts-check

/**
 * @file Renders the web Lexical HTML editor form field.
 */

import {$generateHtmlFromNodes, $generateNodesFromDOM} from "@lexical/html"
import {LinkNode} from "@lexical/link"
import {INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode} from "@lexical/list"
import {LexicalComposer} from "@lexical/react/LexicalComposer"
import {ContentEditable} from "@lexical/react/LexicalContentEditable"
import {LexicalErrorBoundary} from "@lexical/react/LexicalErrorBoundary"
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin"
import {LinkPlugin} from "@lexical/react/LexicalLinkPlugin"
import {ListPlugin} from "@lexical/react/LexicalListPlugin"
import {OnChangePlugin} from "@lexical/react/LexicalOnChangePlugin"
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin"
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {HeadingNode, QuoteNode} from "@lexical/rich-text"
import Text from "@kaspernj/api-maker/build/utils/text"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {useDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"
import {useForm} from "@kaspernj/api-maker/build/form"
import {$getRoot, $insertNodes, COMMAND_PRIORITY_EDITOR, FORMAT_TEXT_COMMAND} from "lexical"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {createRef, memo, useCallback, useEffect, useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {PanResponder, StyleSheet} from "react-native"
import {Pressable, View} from "react-native-propforge"

import {$createAttachmentNode, AttachmentNode, INSERT_ATTACHMENT_COMMAND} from "./attachment-node.web"
import {$createImageNode, ImageNode, INSERT_IMAGE_COMMAND} from "./image-node.web"
import {resolveEditorUploadedFileImageSources} from "./upload-session"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {EditorState, LexicalEditor} from "lexical" */
/** @import {FormInputs} from "@kaspernj/api-maker/build/form" */
/** @import {ChangeEvent, CSSProperties, NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @import {FormHtmlEditorUploadSession} from "./upload-session" */
/** @type {Record<string, object>} */
const styles = {}
/** @type {Record<string, CSSProperties>} */
const contentEditableStyles = {}
const EDITOR_MIN_HEIGHT = 120

// Lexical's <ContentEditable> renders a raw <div contenteditable>, which
// sits outside React Native Web's CSS reset and would otherwise inherit
// the UA default serif font from <body>. Mirror RNW's system stack so the
// editor visually matches sibling <Text>/<TextInput> when no
// <WithDefaultStyle> ancestor provides a project-wide font.
const SYSTEM_FONT_STACK =
  '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif'

const lexicalTheme = {
  heading: {
    h1: "form-html-editor__h1",
    h2: "form-html-editor__h2",
    h3: "form-html-editor__h3"
  },
  link: "form-html-editor__link",
  list: {
    listitem: "form-html-editor__listitem",
    nested: {
      listitem: "form-html-editor__nested-listitem"
    },
    ol: "form-html-editor__ol",
    ul: "form-html-editor__ul"
  },
  paragraph: "form-html-editor__paragraph",
  text: {
    bold: "form-html-editor__bold",
    italic: "form-html-editor__italic",
    underline: "form-html-editor__underline"
  }
}

/**
 * Creates an editor initializer from HTML.
 * @param {string} html HTML fragment to load.
 * @returns {(editor: LexicalEditor) => void} Function that imports HTML into an editor.
 */
function importHtml(html) {
  return (editor) => {
    editor.update(() => {
      const root = $getRoot()
      root.clear()
      if (!html) return
      const parser = new DOMParser()
      const dom = parser.parseFromString(html, "text/html")
      const nodes = $generateNodesFromDOM(editor, dom)
      $insertNodes(nodes)
    })
  }
}

/**
 * Reads a browser file as a data URL.
 * @param {File} file Browser file selected from toolbar, paste, or drop.
 * @returns {Promise<string>} Encoded bytes suitable for an editor image preview.
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name}`))
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error(`Expected ${file.name} to be read as a data URL`))
        return
      }

      resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Extracts the base64 body from a data URL.
 * @param {string} dataUrl Browser data URL.
 * @param {File} file Source file.
 * @returns {{contentBase64: string, contentType: string}} Upload bytes and MIME type.
 * @throws {Error} When the file is not available as base64 data.
 */
function parseFileDataUrl(dataUrl, file) {
  const match = dataUrl.match(/^data:([^;,]+)?;base64,(.*)$/)

  if (!match) {
    throw new Error(`Expected ${file.name} to be read as a base64 data URL`)
  }

  return {
    contentBase64: match[2],
    contentType: match[1] || file.type || "application/octet-stream"
  }
}

/**
 * Checks whether a browser file should be inserted as an image preview.
 * @param {File} file Browser file selected from toolbar, paste, or drop.
 * @returns {boolean} True when the file is an image.
 */
function isImageFile(file) {
  return file.type.startsWith("image/")
}

/**
 * @typedef {object} FormSyncPluginProps
 * @property {ReturnType<typeof useForm>} form Api Maker form controller.
 * @property {string} name Form field name to update.
 */

/**
 * Wires up the Lexical editor's onChange to the surrounding api-maker
 * Form via `form.setValue(name, html)`. Lives inside LexicalComposer so
 * it can use `useLexicalComposerContext`.
 * @param {FormSyncPluginProps} props Form and field binding.
 * @returns {ReactNode} Lexical on-change plugin.
 */
function FormSyncPlugin({form, name}) {
  const onChange = useCallback(
    /**
     * Writes serialized editor HTML into the form.
     * @param {EditorState} _editorState Current editor state.
     * @param {LexicalEditor} editor Editor instance to serialize.
     * @returns {void} Nothing.
     */
    (_editorState, editor) => {
      editor.read(() => {
        const html = $generateHtmlFromNodes(editor, null)
        if (form) form.setValue(name, html)
      })
    },
    [form, name]
  )
  return <OnChangePlugin ignoreSelectionChange onChange={onChange} />
}

/**
 * @typedef {object} ToolbarProps
 * @property {FormHtmlEditorUploadSession | undefined} uploadSession Optional upload session.
 * @property {string} testID Base test identifier for toolbar controls.
 */

/**
 * Renders the formatting toolbar for the Lexical editor. Lives inside
 * LexicalComposer so it can dispatch commands on the editor via
 * `useLexicalComposerContext`.
 * @param {ToolbarProps} props Toolbar configuration.
 * @returns {ReactNode} Formatting toolbar.
 */
function Toolbar({testID, uploadSession}) {
  const [editor] = useLexicalComposerContext()
  const colors = useThemeColors()
  /** @type {RefObject<HTMLInputElement | null>} */
  const fileInputRef = useRef(null)
  const buttonStyle = useMemo(
    () => ({
      alignItems: "center",
      backgroundColor: colors.surfaceMuted,
      borderColor: colors.borderStrong,
      borderRadius: 6,
      borderWidth: 1,
      justifyContent: "center",
      minWidth: 34,
      paddingHorizontal: 10,
      paddingVertical: 6
    }),
    [colors.surfaceMuted, colors.borderStrong]
  )
  const insertFile = useCallback(
    /**
     * Inserts one selected file.
     * @param {File} file Browser file.
     * @returns {Promise<void>} Resolves after the editor node is inserted.
     * @rejects {Error} When uploads are disabled or the file cannot be read.
     */
    async (file) => {
      if (!uploadSession) {
        throw new Error("Upload session is required before inserting editor files")
      }
      const dataUrl = await readFileAsDataUrl(file)
      const parsedDataUrl = parseFileDataUrl(dataUrl, file)
      const filename = file.name
      const pendingUploadId = uploadSession.registerUpload({
        contentBase64: parsedDataUrl.contentBase64,
        contentType: parsedDataUrl.contentType,
        filename,
        sizeBytes: file.size
      })

      if (isImageFile(file)) {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          altText: filename,
          pendingUploadId,
          src: dataUrl
        })
      } else {
        editor.dispatchCommand(INSERT_ATTACHMENT_COMMAND, {
          contentType: parsedDataUrl.contentType,
          filename,
          pendingUploadId,
          sizeBytes: file.size
        })
      }
    },
    [editor, uploadSession]
  )
  const onFilePress = useCallback(() => {
    const fileInput = fileInputRef.current

    if (fileInput) {
      fileInput.click()
    }
  }, [])
  const onFileChange = useCallback(
    /**
     * Handles file selection.
     * @param {ChangeEvent<HTMLInputElement>} event File input change event.
     * @returns {void} Nothing.
     */
    (event) => {
      const files = event.currentTarget.files
      const file = files && files[0]

      if (!file) return

      void insertFile(file)
      event.currentTarget.value = ""
    },
    [insertFile]
  )
  return (
    <View
      style={styles.toolbar ||= {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 6
      }}
      testID={`${testID}/toolbar`}
    >
      <Pressable
        onPress={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
        style={buttonStyle}
        testID={`${testID}/toolbar/bold`}
      >
        <FontAwesome6 color={colors.text} name="bold" size={13} testID={`${testID}/toolbar/bold/icon`} />
      </Pressable>
      <Pressable
        onPress={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
        style={buttonStyle}
        testID={`${testID}/toolbar/italic`}
      >
        <FontAwesome6 color={colors.text} name="italic" size={13} testID={`${testID}/toolbar/italic/icon`} />
      </Pressable>
      <Pressable
        onPress={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
        style={buttonStyle}
        testID={`${testID}/toolbar/underline`}
      >
        <FontAwesome6 color={colors.text} name="underline" size={13} testID={`${testID}/toolbar/underline/icon`} />
      </Pressable>
      <Pressable
        onPress={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        style={buttonStyle}
        testID={`${testID}/toolbar/bulletList`}
      >
        <FontAwesome6 color={colors.text} name="list-ul" size={13} testID={`${testID}/toolbar/bulletList/icon`} />
      </Pressable>
      <Pressable
        onPress={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        style={buttonStyle}
        testID={`${testID}/toolbar/numberedList`}
      >
        <FontAwesome6 color={colors.text} name="list-ol" size={13} testID={`${testID}/toolbar/numberedList/icon`} />
      </Pressable>
      {uploadSession &&
        <Pressable
          onPress={onFilePress}
          style={buttonStyle}
          testID={`${testID}/toolbar/file`}
        >
          <FontAwesome6 color={colors.text} name="paperclip" size={13} testID={`${testID}/toolbar/file/icon`} />
        </Pressable>
      }
      {uploadSession &&
        <input
          data-testid={`${testID}/toolbar/fileInput`}
          onChange={onFileChange}
          ref={fileInputRef}
          style={styles.fileInput ||= {
            height: 1,
            left: -10000,
            opacity: 0,
            position: "absolute",
            top: 0,
            width: 1
          }}
          type="file"
        />
      }
    </View>
  )
}

/**
 * @typedef {object} UploadPluginProps
 * @property {FormHtmlEditorUploadSession} uploadSession Session that owns unsaved files.
 */

/**
 * Registers file insertion, paste, and drop handling.
 * @param {UploadPluginProps} props Upload plugin configuration.
 * @returns {ReactNode} Null plugin output.
 */
function UploadPlugin({uploadSession}) {
  const [editor] = useLexicalComposerContext()
  const insertFile = useCallback(
    /**
     * Inserts one browser file.
     * @param {File} file Browser file.
     * @returns {Promise<void>} Resolves after the node is inserted.
     * @rejects {Error} When the file cannot be read.
     */
    async (file) => {
      const dataUrl = await readFileAsDataUrl(file)
      const parsedDataUrl = parseFileDataUrl(dataUrl, file)
      const filename = file.name
      const pendingUploadId = uploadSession.registerUpload({
        contentBase64: parsedDataUrl.contentBase64,
        contentType: parsedDataUrl.contentType,
        filename,
        sizeBytes: file.size
      })

      if (isImageFile(file)) {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          altText: filename,
          pendingUploadId,
          src: dataUrl
        })
      } else {
        editor.dispatchCommand(INSERT_ATTACHMENT_COMMAND, {
          contentType: parsedDataUrl.contentType,
          filename,
          pendingUploadId,
          sizeBytes: file.size
        })
      }
    },
    [editor, uploadSession]
  )
  const onPaste = useCallback(
    /**
     * Inserts pasted files.
     * @param {ClipboardEvent} event Paste event.
     * @returns {void} Nothing.
     */
    (event) => {
      const files = Array.from(event.clipboardData?.files || [])

      if (files.length < 1) return

      event.preventDefault()
      for (const file of files) {
        void insertFile(file)
      }
    },
    [insertFile]
  )
  const onDrop = useCallback(
    /**
     * Inserts dropped files.
     * @param {DragEvent} event Drop event.
     * @returns {void} Nothing.
     */
    (event) => {
      const files = Array.from(event.dataTransfer?.files || [])

      if (files.length < 1) return

      event.preventDefault()
      for (const file of files) {
        void insertFile(file)
      }
    },
    [insertFile]
  )

  useEffect(() => editor.registerCommand(
    INSERT_IMAGE_COMMAND,
    (payload) => {
      $insertNodes([$createImageNode(payload)])
      return true
    },
    COMMAND_PRIORITY_EDITOR
  ), [editor])

  useEffect(() => editor.registerCommand(
    INSERT_ATTACHMENT_COMMAND,
    (payload) => {
      $insertNodes([$createAttachmentNode(payload)])
      return true
    },
    COMMAND_PRIORITY_EDITOR
  ), [editor])

  useEffect(() => {
    return editor.registerRootListener((rootElement, previousRootElement) => {
      if (previousRootElement) {
        previousRootElement.removeEventListener("paste", onPaste)
        previousRootElement.removeEventListener("drop", onDrop)
      }

      if (rootElement) {
        rootElement.addEventListener("paste", onPaste)
        rootElement.addEventListener("drop", onDrop)
      }
    })
  }, [editor, onDrop, onPaste])

  return null
}

/**
 * @typedef {object} InitialHtmlPluginProps
 * @property {string | null | undefined} html Initial HTML fragment.
 */

/**
 * Loads the initial HTML into the editor exactly once after the
 * editor mounts. Doing it from a plugin (instead of via `editorState`
 * in the composer config) lets us call `$generateNodesFromDOM` which
 * needs an existing editor instance.
 * @param {InitialHtmlPluginProps} props Initial content configuration.
 * @returns {ReactNode} Null after initializing the editor.
 */
function InitialHtmlPlugin({html}) {
  const [editor] = useLexicalComposerContext()
  const loadedRef = useRef(false)
  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    importHtml(html ?? "")(editor)
  }, [editor, html])
  return null
}

/**
 * @typedef {object} FormHtmlEditorProps
 * @property {number} [autoGrowMaxHeight] Maximum automatic editor height before scrolling.
 * @property {string} [defaultValue] Initial editor HTML.
 * @property {FormHtmlEditorUploadSession} [uploadSession] Optional upload session.
 * @property {string} label Field label.
 * @property {string} name Form field name.
 * @property {boolean} [resizable] Whether users can manually resize the editor.
 * @property {string} testID Base test identifier.
 */

/**
 * @typedef {object} FormHtmlEditorState
 * @property {number | null} manualHeight User-resized editor height.
 * @property {Error | null} resolveError Default-value resolution error.
 * @property {string | null} resolvedDefaultValue Initial HTML with uploaded image sources resolved.
 */

/**
 * Rethrows Lexical editor errors.
 * @param {Error} error Error raised by Lexical.
 * @throws {Error} Always rethrows the given error.
 */
function rethrowLexicalError(error) {
  throw error
}

/**
 * Web-only HTML editor backed by Lexical. Hooks into the surrounding
 * api-maker Form so the rendered HTML reaches `form.asObject()`
 * under `props.name`.
 * @augments {ShapeComponent<FormHtmlEditorProps, FormHtmlEditorState>}
 */
class FormHtmlEditor extends ShapeComponent {
  /** @type {FormInputs | null} */
  form = null

  /** @type {RefObject<HTMLDivElement | null>} */
  contentEditableRef = createRef()

  /** @type {{color?: string, fontFamily?: string, fontSize?: number}} */
  defaultText = {}

  resizeStartHeight = EDITOR_MIN_HEIGHT

  resizePanResponder = PanResponder.create({
    onMoveShouldSetPanResponder: () => this.p.resizable,
    onPanResponderGrant: () => {
      this.resizeStartHeight = this.currentResizeHeight()
    },
    onPanResponderMove: (_event, gestureState) => {
      if (!this.p.resizable) return

      this.s.manualHeight = Math.max(
        EDITOR_MIN_HEIGHT,
        Math.ceil(this.resizeStartHeight + gestureState.dy)
      )
    },
    onStartShouldSetPanResponder: () => this.p.resizable
  })

  /** @type {FormHtmlEditorState} */
  state = {
    manualHeight: null,
    resolveError: /** @type {Error | null} */ null,
    resolvedDefaultValue: null
  }
  static propTypes = propTypesExact({
    autoGrowMaxHeight: PropTypes.number,
    defaultValue: PropTypes.string,
    uploadSession: PropTypes.object,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    resizable: PropTypes.bool,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    autoGrowMaxHeight: undefined,
    defaultValue: undefined,
    uploadSession: undefined,
    resizable: false
  }

  /** Initializes form integration and default text styling. */
  setup() {
    this.form = useForm()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const defaultStyle = useDefaultStyle()
    this.defaultText = StyleSheet.flatten(defaultStyle?.Text) || {}
    const defaultValue = this.p.defaultValue
    const fieldName = this.p.name
    useEffect(() => {
      let cancelled = false
      const resolveDefaultValue = async () => {
        try {
          const resolvedDefaultValue = await resolveEditorUploadedFileImageSources(defaultValue || "")

          if (cancelled) return

          this.s.resolveError = null
          this.s.resolvedDefaultValue = resolvedDefaultValue
        } catch (error) {
          if (cancelled) return
          this.s.resolveError = error instanceof Error ? error : new Error(String(error))
        }
      }

      this.s.resolvedDefaultValue = null
      void resolveDefaultValue()

      return () => {
        cancelled = true
      }
    }, [defaultValue])
    const resolvedDefaultValue = this.s.resolvedDefaultValue
    useEffect(() => {
      if (this.form && resolvedDefaultValue !== null) {
        this.form.setValue(fieldName, resolvedDefaultValue)
      }
    }, [fieldName, resolvedDefaultValue])
  }

  /**
   * Renders the HTML editor field.
   * @returns {ReactNode} Editor form field.
   * @throws {Error} When persisted editor image references cannot be resolved.
   */
  render() {
    if (this.s.resolveError) throw this.s.resolveError
    const {autoGrowMaxHeight, label, name, resizable, testID, uploadSession} = this.p
    const {defaultText} = this
    const resizePanResponder = this.tt.resizePanResponder
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const contentColor = defaultText.color ?? colors.text
    const contentFontFamily = defaultText.fontFamily ?? SYSTEM_FONT_STACK
    const contentFontSize = defaultText.fontSize ?? 16
    const contentEditableHeight = this.s.manualHeight || undefined
    const resolvedDefaultValue = this.s.resolvedDefaultValue
    const initialConfig = {
      namespace: `FormHtmlEditor:${name}`,
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AttachmentNode, ImageNode],
      onError: rethrowLexicalError,
      theme: lexicalTheme
    }
    return (
      <View>
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 600
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        <View
          style={styles[`editorFrame-${themeName}`] ||= {
            borderColor: colors.borderStrong,
            borderRadius: 6,
            borderWidth: 1,
            marginTop: 6,
            padding: 8
          }}
          testID={`${testID}/frame`}
        >
          {resolvedDefaultValue !== null &&
            <LexicalComposer initialConfig={initialConfig}>
              <Toolbar testID={testID} uploadSession={uploadSession} />
              <View style={styles.editorContent ||= {position: "relative"}}>
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable
                      ariaLabel={label}
                      className="form-html-editor__content"
                      data-testid={testID}
                      ref={this.contentEditableRef}
                      style={contentEditableStyles[`contentEditable-${contentColor}-${contentFontFamily}-${contentFontSize}-${contentEditableHeight ?? "auto"}-${autoGrowMaxHeight ?? "none"}-${resizable ? "resizable" : "fixed"}`] ||= {
                        boxSizing: "border-box",
                        color: contentColor,
                        fontFamily: contentFontFamily,
                        fontSize: contentFontSize,
                        height: contentEditableHeight,
                        maxHeight: contentEditableHeight ? undefined : autoGrowMaxHeight,
                        minHeight: EDITOR_MIN_HEIGHT,
                        outline: "none",
                        overflowY: contentEditableHeight || autoGrowMaxHeight ? "auto" : undefined,
                        padding: 8,
                        paddingRight: resizable ? 38 : 8
                      }}
                    />
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                  placeholder={
                    <div
                      style={{
                        color: colors.placeholder,
                        fontFamily: contentFontFamily,
                        fontSize: contentFontSize,
                        left: 8,
                        pointerEvents: "none",
                        position: "absolute",
                        top: 8
                      }}
                    >
                      {label}
                    </div>
                  }
                />
                {resizable &&
                  <View
                    {...resizePanResponder.panHandlers}
                    style={styles.resizeHandle ||= {
                      alignItems: "center",
                      bottom: 4,
                      height: 28,
                      justifyContent: "center",
                      position: "absolute",
                      right: 4,
                      width: 28,
                      zIndex: 1
                    }}
                    testID={`${testID}/resizeHandle`}
                  >
                    <FontAwesome6
                      color={colors.textMuted}
                      name="arrows-up-down-left-right"
                      size={12}
                      testID={`${testID}/resizeHandle/icon`}
                    />
                  </View>
                }
              </View>
              <HistoryPlugin />
              <ListPlugin />
              <LinkPlugin />
              {uploadSession && <UploadPlugin uploadSession={uploadSession} />}
              <InitialHtmlPlugin html={resolvedDefaultValue} />
              {this.form && <FormSyncPlugin form={this.form} name={name} />}
            </LexicalComposer>
          }
        </View>
      </View>
    )
  }

  /**
   * Returns the current editor height before manual resizing starts.
   * @returns {number} Current editable area height.
   */
  currentResizeHeight() {
    const contentEditable = this.contentEditableRef.current

    if (contentEditable) {
      return contentEditable.getBoundingClientRect().height
    }

    return this.s.manualHeight || this.p.autoGrowMaxHeight || EDITOR_MIN_HEIGHT
  }

}

/** @type {NamedExoticComponent<FormHtmlEditorProps>} */
const FormHtmlEditorComponent = memo(shapeComponent(FormHtmlEditor))

export default FormHtmlEditorComponent
